import { useState, useEffect, type ComponentType } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Package, Clock, ShieldAlert, Wrench,
  PlusCircle, List, ArrowRight, AlertTriangle, Lightbulb,
  type LucideProps,
} from 'lucide-react';
import type { AxiosError } from 'axios';
import { toast } from 'sonner';
import { extractErrorMessage } from '../lib/errors.js';
import { useSellerAuth } from '../context/SellerAuthContext.js';
import { getSellerDevices, getSellerDashboard } from '../api/seller.js';
import { StatusBadge } from '../components/ui/Badge.js';
import { Skeleton } from '../components/ui/Skeleton.js';
import { inferProductType } from '../lib/product.js';
import { ProductBadge } from '../lib/ProductBadge.js';

function useCountUp(target: number) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (target === 0) { setCount(0); return; }
    let current = 0;
    const steps = 20;
    const inc = target / steps;
    const timer = setInterval(() => {
      current = Math.min(current + inc, target);
      setCount(Math.round(current));
      if (current >= target) clearInterval(timer);
    }, 30);
    return () => clearInterval(timer);
  }, [target]);
  return count;
}

interface StatCardProps {
  label:      string;
  value:      number;
  icon:       ComponentType<LucideProps>;
  accent:     string;
  topAccent:  string;
  isLoading:  boolean;
  delay:      number;
}

function StatCard({ label, value, icon: Icon, accent, topAccent, isLoading, delay }: StatCardProps) {
  const count = useCountUp(isLoading ? 0 : value);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, delay }}
      className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm"
    >
      <div className={`h-0.5 ${topAccent}`} />
      <div className="p-5">
        <div className="flex items-start justify-between mb-4">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{label}</p>
          <span className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent}`}>
            <Icon className="w-4 h-4" />
          </span>
        </div>
        {isLoading
          ? <Skeleton className="h-8 w-12" />
          : <p className="text-3xl font-bold text-slate-900 tabular-nums">{count}</p>
        }
      </div>
    </motion.div>
  );
}

const STAT_CARDS = [
  { label: 'Total',             key: 'total'                as const, icon: Package,     accent: 'bg-slate-100 text-slate-600',   topAccent: 'bg-slate-400'  },
  { label: 'Pending',           key: 'pending'              as const, icon: Clock,       accent: 'bg-amber-50 text-amber-600',    topAccent: 'bg-amber-400'  },
  { label: 'Warranty expiring', key: 'warrantyExpiringSoon' as const, icon: ShieldAlert, accent: 'bg-rose-50 text-rose-600',      topAccent: 'bg-rose-400'   },
  { label: 'Open complaints',   key: 'openComplaints'       as const, icon: Wrench,      accent: 'bg-blue-50 text-blue-600',      topAccent: 'bg-blue-500'   },
];

const QUICK_ACTIONS = [
  { to: '/devices/register/cop-lop', label: 'Register COP-LOP',    desc: 'Pre-register a lift. Invoice required; registration mandatory before install.',          icon: PlusCircle, accent: 'bg-indigo-50 text-indigo-600' },
  { to: '/devices/register/m8',      label: 'Register M8 Light',   desc: 'Record a ceiling or stair light registration. Can be done before or after install.',     icon: Lightbulb,  accent: 'bg-teal-50 text-teal-600'     },
  { to: '/devices',                  label: 'Device List',         desc: 'Browse, search and export all registered devices.',                                       icon: List,       accent: 'bg-blue-50 text-blue-600'     },
  { to: '/services',                 label: 'Services Management', desc: 'View and log service requests across all your devices.',                                  icon: Wrench,     accent: 'bg-amber-50 text-amber-600'   },
];

export function DashboardPage() {
  const { seller } = useSellerAuth();
  const navigate   = useNavigate();

  const devicesQuery = useQuery({
    queryKey: ['seller-devices'],
    queryFn:  getSellerDevices,
  });
  const dashboardQuery = useQuery({
    queryKey: ['seller-dashboard'],
    queryFn:  getSellerDashboard,
  });

  useEffect(() => {
    if (devicesQuery.isError) {
      const msg = extractErrorMessage(
        devicesQuery.error as AxiosError<{ message?: string }> | undefined,
        'Could not load your devices. Please refresh.',
      );
      toast.error(msg);
    }
  }, [devicesQuery.isError, devicesQuery.error]);

  const registrations = devicesQuery.data?.registrations ?? [];
  const stats = dashboardQuery.data ?? { total: 0, pending: 0, warrantyExpiringSoon: 0, openComplaints: 0 };
  const isLoading = devicesQuery.isLoading || dashboardQuery.isLoading;

  const recent = [...registrations]
    .sort((a, b) => new Date(b.registeredAt).getTime() - new Date(a.registeredAt).getTime())
    .slice(0, 5);

  if (!seller) return null;

  return (
    <div className="p-6 max-w-6xl">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-xl font-bold text-slate-900">
          Good day, {seller.name.split(' ')[0]}
        </h1>
        <p className="text-sm text-slate-500 mt-0.5">Here&apos;s what&apos;s happening with your devices.</p>
      </div>

      {/* Blocked warning */}
      {seller.status === 'blocked' && (
        <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-6 text-sm text-red-700">
          <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />
          Your account is blocked. Contact Haron support to restore access.
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {STAT_CARDS.map((card, i) => (
          <StatCard
            key={card.key}
            label={card.label}
            value={stats[card.key]}
            icon={card.icon}
            accent={card.accent}
            topAccent={card.topAccent}
            isLoading={isLoading}
            delay={i * 0.05}
          />
        ))}
      </div>

      {/* Quick actions */}
      <div className="mb-8">
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">Quick actions</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {QUICK_ACTIONS.map(({ to, label, desc, icon: Icon, accent }) => (
            <Link
              key={to}
              to={to}
              className="group flex flex-col bg-white border border-slate-200 rounded-xl p-5 hover:border-blue-300 hover:shadow-md hover:-translate-y-0.5 transition-all duration-150"
            >
              <div className="flex items-start justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-0.5 transition-all duration-150" />
              </div>
              <p className="text-sm font-semibold text-slate-800 group-hover:text-blue-700 transition-colors mb-1">{label}</p>
              <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent registrations */}
      {registrations.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide">Recent Registrations</p>
            <button
              type="button"
              onClick={() => navigate('/devices')}
              className="text-xs font-medium text-blue-600 hover:text-blue-700 transition-colors"
            >
              View all →
            </button>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Serial</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Product</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Customer</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Registered</th>
                </tr>
              </thead>
              <tbody>
                {isLoading
                  ? [0, 1, 2].map((i) => (
                    <tr key={i} className="border-b border-slate-50">
                      {[0, 1, 2, 3, 4].map((j) => (
                        <td key={j} className="px-5 py-3.5">
                          <div className="h-4 bg-slate-100 rounded animate-pulse w-24" />
                        </td>
                      ))}
                    </tr>
                  ))
                  : recent.map((r, i) => (
                    <tr
                      key={r.registrationId}
                      onClick={() => navigate(`/devices/${r.registrationId}`)}
                      className={`cursor-pointer hover:bg-slate-50 transition-colors ${i < recent.length - 1 ? 'border-b border-slate-50' : ''}`}
                    >
                      <td className="px-5 py-3.5 font-mono text-slate-700 text-xs">{r.serialNumber}</td>
                      <td className="px-5 py-3.5"><ProductBadge type={inferProductType(r.serialNumber)} /></td>
                      <td className="px-5 py-3.5 text-slate-600 text-sm">{r.customerMobile}</td>
                      <td className="px-5 py-3.5"><StatusBadge status={r.status} /></td>
                      <td className="px-5 py-3.5 text-slate-400 text-xs tabular-nums">
                        {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                          .format(new Date(r.registeredAt))}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
